import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { formatLocalDateTime } from '../utils/dateTime';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState([
    { title: 'Должники', count: 0, color: '#e74c3c', loading: true, icon: '👥', route: '/debtors' },
    { title: 'Организаторы', count: 0, color: '#3498db', loading: true, icon: '🏢', route: '/organizers' },
    { title: 'Торги (всего)', count: 0, color: '#2ecc71', loading: true, icon: '⚖️', route: '/auctions' },
    { title: 'Торги активные', count: 0, color: '#16a085', loading: true, icon: '▶️', route: '/auctions' },
    { title: 'Лоты (всего)', count: 0, color: '#9b59b6', loading: true, icon: '📦', route: '/lots' },
    { title: 'Мои документы', count: 0, color: '#f39c12', loading: true, icon: '📄', route: '/documents' },
  ]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [tablesHeight, setTablesHeight] = useState(310);
  const statsContainerRef = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const [debtors, organizers, auctions, lots, documents, users] = await Promise.all([
        apiService.getDebtors(),
        apiService.getOrganizers(), 
        apiService.getAuctions(),
        apiService.getLots(),
        apiService.getDocuments(),
        user?.is_staff ? apiService.getUsers() : Promise.resolve({ data: { count: 0 } }),
      ]);

      const auctionItems = Array.isArray(auctions.data?.results) ? auctions.data.results : [];
      const activeAuctionStatuses = new Set(['pending', 'applications', 'applications_closed', 'in_progress']);
      const activeAuctionsCount = auctionItems.filter((item) => activeAuctionStatuses.has(String(item?.status || '').toLowerCase())).length;

      const nextStats = [
        { title: 'Должники', count: debtors.data.count || 0, color: '#e74c3c', icon: '👥', route: '/debtors' },
        { title: 'Организаторы', count: organizers.data.count || 0, color: '#3498db', icon: '🏢', route: '/organizers' },
        { title: 'Торги (всего)', count: auctions.data.count || 0, color: '#2ecc71', icon: '⚖️', route: '/auctions' },
        { title: 'Торги активные', count: activeAuctionsCount, color: '#16a085', icon: '▶️', route: '/auctions' },
        { title: 'Лоты (всего)', count: lots.data.count || 0, color: '#9b59b6', icon: '📦', route: '/lots' },
        { title: 'Мои документы', count: documents.data.count || 0, color: '#f39c12', icon: '📄', route: '/documents' },
      ];

      if (user?.is_staff) {
        nextStats.push({
          title: 'Пользователи',
          count: users.data?.count || 0,
          color: '#34495e',
          icon: '👤',
          route: '/users',
        });
      }

      setStats(nextStats);

    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
      setStats(prev => prev.map(stat => ({ ...stat, loading: false })));
    }
  }, [user?.is_staff]);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const response = await apiService.getActivityLogs('');
      const rawList = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.results) ? response.data.results : []);
      const filtered = rawList.filter((item) => String(item?.action || '').toLowerCase() !== 'api_call');
      setRecentActivity(filtered.slice(0, 20));
    } catch (error) {
      console.error('Ошибка загрузки активности:', error);
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadActivity();
  }, [loadStats, loadActivity]);

  useLayoutEffect(() => {
    const recalcHeight = () => {
      const container = statsContainerRef.current;
      if (!container) return;
      const targetTitle = user?.is_staff ? 'Пользователи' : 'Мои документы';
      const targetRow = container.querySelector(`tr[data-stat-title="${targetTitle}"]`);
      if (!targetRow) return;

      const containerRect = container.getBoundingClientRect();
      const rowRect = targetRow.getBoundingClientRect();
      const nextHeight = Math.ceil(rowRect.bottom - containerRect.top);
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;

      const safeHeight = Math.max(220, Math.min(nextHeight, 700));
      setTablesHeight(prev => (prev === safeHeight ? prev : safeHeight));
    };

    recalcHeight();

    const resizeObserver = new ResizeObserver(() => recalcHeight());
    if (statsContainerRef.current) resizeObserver.observe(statsContainerRef.current);
    window.addEventListener('resize', recalcHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', recalcHeight);
    };
  }, [stats, user?.is_staff]);

  const tableContainerStyle = {
    height: `${tablesHeight}px`,
    maxHeight: `${tablesHeight}px`,
  };

  return (
    <div className="home">
      <div className="hero">
        <div className="hero-content">
          <h1>Добро пожаловать, {user?.full_name || 'Пользователь'}!</h1>
          <p className="hero-subtitle">Система управления торгами для арбитражных управляющих и организаторов торгов</p>
          <div className="hero-stats">
            <span>📅 Сегодня: {new Date().toLocaleDateString('ru-RU', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="stats-section">
          <div className="section-header">
            <h2>Общая статистика</h2>
          </div>
          <div className="stats-table-container" ref={statsContainerRef} style={tableContainerStyle}>
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Категория</th>
                  <th>Всего</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((stat, index) => (
                  <tr
                    key={index}
                    data-stat-title={stat.title}
                    className={stat.route ? 'stats-row-clickable' : ''}
                    onClick={() => stat.route && navigate(stat.route)}
                  >
                    <td>
                      <div className="category-cell">
                        <span className="category-icon">{stat.icon}</span>
                        <span className="category-name">{stat.title}</span>
                      </div>
                    </td>
                    <td className="count-cell">
                      <span className="count-number" style={{ color: stat.color }}>
                        {stat.loading ? '...' : stat.count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="activity-section">
          <div className="section-header section-header--plain">
            <h2>Последняя активность</h2>
          </div>
          <div className="stats-table-container activity-table-container" style={tableContainerStyle}>
            {recentActivity.length > 0 ? (
              <table className="stats-table activity-table">
                <thead>
                  <tr>
                    <th>Дата и время</th>
                    {user?.is_staff && <th>Пользователь</th>}
                    <th>Действие</th>
                    <th>Сущность</th>
                    <th>Объект</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((activity) => (
                    <tr key={activity.id}>
                      <td>{formatLocalDateTime(activity.created_at)}</td>
                      {user?.is_staff && <td>{activity.actor_name || '—'}</td>}
                      <td>{activity.action_label}</td>
                      <td>{activity.entity_label}</td>
                      <td>{activity.object_repr || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="activity-empty">
                <span className="empty-icon">🔍</span>
                <p>{activityLoading ? 'Загрузка активности...' : 'Активности пока нет'}</p>
                <small>Начните работу с создания торгов или добавления должников</small>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bottom-grid">
        <div className="help-section">
          <div className="help-card">
            <h3>💡 Полезные советы</h3>
            <ul>
              <li>Начните с добавления должников и организаторов торгов</li>
              <li>Создайте торги и добавьте к ним лоты для продажи</li>
              <li>Используйте шаблоны для быстрого создания документов</li>
              <li>Следите за статусами торгов в соответствующем разделе</li>
            </ul>
          </div>
        </div>

        <div className="create-card-section">
          <div className="create-card">
            <h3>⚡ Быстрый старт</h3>
            <p>Создайте новые торги и переходите к оформлению карточки.</p>
            <button
              className="help-create-button"
              onClick={() => navigate('/auctions', { state: { openCreateModal: true } })}
            >
              <span className="help-create-icon">+</span>
              <span>Создать торги</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
